import { List, Record } from "immutable";
import _ from "lodash";

import {
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  DIRECTION_TOP,
  LAYOUT_VIEW,
  MIXED_VIEWS,
  ORIENTATION_COLUMN,
  ORIENTATION_ROW} from "../../../constants/identifiers";
import { createFromJS } from "../../../types";
import { appStateSelectors } from "..";
import { actionTypes } from "../actions";
import { getFirstViewFromLayout,nextViewInLayout } from "./utils";

/**
 *
 * @param {Map} prevGlobalState
 * @param {Object} action
 * @param {string} viewId
 * @returns {Map}
 */
const getViewTypesFollowingAction = (prevGlobalState, action, viewId) => {
  const viewTypesMap = appStateSelectors.getOpenViewsById(prevGlobalState).map(v => v.get("viewType"));
  switch (action.type) {
    case actionTypes.SPLIT_VIEW_HORIZONTAL:
    case actionTypes.SPLIT_VIEW_VERTICAL: {
      return viewTypesMap.set(viewId, viewTypesMap.get(action.splittingViewId));
    }
    case actionTypes.POPOUT_VIEW:
    case actionTypes.REMOVE_VIEW:
      return viewTypesMap.filter((v, vId) => vId !== viewId);
    case actionTypes.REMOVE_OTHER_VIEWS: {
      return viewTypesMap.filter((_v, vId) => vId === viewId);
    }
    case actionTypes.SWITCH_VIEW_TYPE: {
      return viewTypesMap.set(viewId, action.viewType);
    }
    default:
      return viewTypesMap;
  }
};

const newSubLayout = (orientation, children) => {
  const childList = List(children);
  return createFromJS.ViewLayoutRecord({
    orientation,
    children: childList
  });
};

/**
 *
 * @param {LayoutRecord} layout
 * @param {number} viewId
 * @param {string} direction DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_LEFT, DIRECTION_TOP, ORIENTATION_COLUMN, ORIENTATION_ROW
 */
const addView = (layout, viewId, direction) => {
  const orientation =
    direction === DIRECTION_LEFT || direction === DIRECTION_RIGHT || direction === ORIENTATION_COLUMN
      ? ORIENTATION_COLUMN
      : ORIENTATION_ROW;
  const first = direction === DIRECTION_LEFT || direction === DIRECTION_TOP;
  const children = first ? [viewId, layout] : [layout, viewId];
  return newSubLayout(orientation, children, MIXED_VIEWS);
};

const removeSublayout = (l, viewId, treeDepth) => {
  // Recursive bail: filter out any leaf node that is the view we are trying to remove.
  if (!Record.isRecord(l)) {
    if (l === viewId) {
      return null;
    }
    return l;
  }

  // Need to consider children within this layout.
  // First, recursively call removeSublayout for each child, and then filter out any nulls
  // caused by the above leaf check.
  return l.update("children", chil =>
    chil.map(c => removeSublayout(c, viewId, treeDepth + 1)).filterNot(c => _.isNil(c))
  );
};

const splitSublayout = (l, newViewId, splittingViewId, splitOrientation) => {
  // If we hit a leaf node, don't touch it, just return it.
  if (!Record.isRecord(l)) {
    return l;
  }
  // If we can find the exact viewId as a child id in this one,
  // add the new one next to it.
  const children = l.get("children");
  const foundIndex = children.indexOf(splittingViewId);
  if (foundIndex >= 0) {
    const reverseOrientation = l.get("orientation") === splitOrientation;
    if (reverseOrientation) {
      const useOrientation = l.get("orientation") === ORIENTATION_ROW ? ORIENTATION_COLUMN : ORIENTATION_ROW;
      const newLayout = newSubLayout(useOrientation, [splittingViewId, newViewId]);
      return l.update("children", c => c.set(foundIndex, newLayout));
    } else {
      return l.update("children", c => c.insert(foundIndex + 1, newViewId));
    }
  }
  // Otherwise, we need to call splitSublayout recursively.
  return l.update("children", lc => lc.map(c => splitSublayout(c, newViewId, splittingViewId, splitOrientation)));
};

// Consolidate layout down to the simplest available representation, while promoting adjacent similar views into their
// own splits so that they can share headers and scrolling.
const consolidateLayout = (l, treeDepth, viewTypes, scrollingLocked) => {
  // Recursive bail: Return leaf nodes as they are.
  if (!Record.isRecord(l)) {
    return l;
  }

  // First, consolidate the children
  let children = l.get("children").map(c => consolidateLayout(c, treeDepth + 1, viewTypes, scrollingLocked));

  // If there is only one child view, then this current view is unnecessary, so return the child directly.
  if (children.count() === 1) {
    const soleChild = children.get(0);
    // Except we need to ensure there is an item at the base level
    if (treeDepth > 0 || Record.isRecord(soleChild)) {
      return soleChild;
    }
  }

  // If any of the children have the same orientation as me, roll them into me. Note that this may be reversed by the next step.
  const myOrientation = l.get("orientation");
  let rolledChildren = [];
  children.forEach(c => {
    if (Record.isRecord(c) && c.get("orientation") === myOrientation) {
      rolledChildren.push(...c.get("children").toArray());
    } else {
      rolledChildren.push(c);
    }
  });

  children = List(rolledChildren);

  // Finally, unless all of my children are of the same view type, group adjacent related children into their own group once more.
  // Note that this reverses the step above, which means every time we run this algorithm, we are effectively undoing and redoing the same work.
  // But since this should be done rarely, fast, and result in the same layout, there shouldn't be any harmful side effects from doing so.

  const viewTypesOfChildren = children.map(c => (Record.isRecord(c) ? null : viewTypes[c])).toArray();
  const uniqueTypes = _.uniq(viewTypesOfChildren);
  const firstType = uniqueTypes[0];

  const regroupRequired = uniqueTypes.length > 1 || !appStateSelectors.canViewTypeSyncScrolling(firstType);

  if (regroupRequired && scrollingLocked) {
    // Fill mergedChildren with a new list of children, grouping those that can go together
    let mergedChildren = [];

    // This group has more than one type.
    // See if we can find any to group together.
    let groupType = null;
    let groupChildren = []; // building list
    const childCount = children.count();

    for (let i = 0; i < childCount; i++) {
      const child = children.get(i);
      const myType = viewTypesOfChildren[i];
      const addToGroup = myType && myType === groupType && appStateSelectors.canViewTypeSyncScrolling(myType);
      if (addToGroup) {
        groupChildren.push(child);
      } else {
        // We are not adding to the group, so we need to first commit the old group.
        if (groupChildren.length > 1) {
          mergedChildren.push(newSubLayout(myOrientation, groupChildren, groupType));
        } else if (groupChildren.length === 1) {
          mergedChildren.push(groupChildren[0]);
        }

        // If this item is a leaf child, start creating a new group with it to see if we can merge in any others
        if (myType !== LAYOUT_VIEW) {
          groupChildren = [child];
          groupType = myType;
        } else {
          // Otherwise, it is a nested view also, so just add it into our direct output and reset the group for next time.
          mergedChildren.push(child);
          groupChildren = [];
          groupType = null;
        }
      }
    }
    // Outside of the loop: add our last accumulated group into children
    if (groupChildren.length > 1) {
      mergedChildren.push(newSubLayout(myOrientation, groupChildren, groupType));
    } else if (groupChildren.length === 1) {
      mergedChildren.push(groupChildren[0]);
    }
    // Now update our children array.
    children = List(mergedChildren);
  }
  // Otherwise, return our full list of children.
  return l.merge({
    children: children
  });
};

const updateOrientation = (layout, orientation) => {
  let newLayout = layout.set("orientation", orientation);
  newLayout = newLayout.update("children", children =>
    children.map(c => (typeof c === "string" ? c : updateOrientation(c, orientation)))
  );
  return newLayout;
};

const setViewsLayoutOfNumber = (
  number,
  layoutState,
  viewsCount,
  activeViewId,
  newViewIds,
  orientation = ORIENTATION_ROW
) => {
  let newLayout = layoutState;
  if (viewsCount < number) {
    const diff = number - viewsCount;
    for (let i = 0; i < diff; i++) {
      newLayout = newLayout.update("children", c => c.push(newViewIds[i]));
    }
  } else if (viewsCount > number) {
    let nextViewId = activeViewId;
    let keepIds = [];
    for (let i = 0; i < number - 1; i++) {
      nextViewId = nextViewInLayout(layoutState, nextViewId) || getFirstViewFromLayout(layoutState);
      keepIds.push(nextViewId);
    }
    newLayout = createFromJS.ViewLayoutRecord({
      orientation: orientation,
      children: [activeViewId]
    });
    keepIds.forEach(newId => {
      newLayout = newLayout.update("children", c => c.push(newId));
    });
  }
  // Fix orientations
  newLayout = updateOrientation(newLayout, orientation);
  return newLayout;
};

const layout = (state = createFromJS.ViewLayoutRecord(), action, prevGlobalState) => {
  // For now, let's just dump it in the parent of the active view...?
  const prevAppState = prevGlobalState.get("appState");
  const activeViewId = appStateSelectors.getActiveViewId(prevGlobalState);
  const actionViewId = _.isNil(action.viewId) ? activeViewId : action.viewId;
  const childCount = state.get("children").count();
  const viewsCount = appStateSelectors.getOpenViewCount(prevGlobalState);

  // After responding to some actions, we want to be able to consolidate the layout to arrive at the
  // most efficient arrangement.
  // Rather than repeating code, we do this once at the end by caching the result into newLayout first.
  // Default to storing state to avoid needing to set it in multiple case statements below.
  let newLayout = state;
  let consolidateRequired = false;
  let scrollingLocked = prevAppState.scrollingIsLocked;

  switch (action.type) {
    case actionTypes.REPLACE_OPEN_VIEWS:
      return createFromJS.OpenViewsRecord(action.openViews).layout;
    case actionTypes.ADD_VIEW: {
      newLayout = addView(state, action.viewId, action.direction, scrollingLocked);
      consolidateRequired = true;
      break;
    }
    case actionTypes.SPLIT_VIEW_HORIZONTAL: {
      newLayout = splitSublayout(state, action.viewId, action.splittingViewId, ORIENTATION_COLUMN);
      consolidateRequired = true;
      break;
    }
    case actionTypes.SPLIT_VIEW_VERTICAL: {
      newLayout = splitSublayout(state, action.viewId, action.splittingViewId, ORIENTATION_ROW);
      consolidateRequired = true;
      break;
    }
    case actionTypes.POPOUT_VIEW:
    case actionTypes.REMOVE_VIEW: {
      // Do not remove the last child
      if (childCount !== 1) {
        newLayout = removeSublayout(state, actionViewId, 0);
        consolidateRequired = true;
      }
      break;
    }
    case actionTypes.SET_LAYOUT_SINGLE_VIEW: {
      return viewsCount > 1
        ? createFromJS.ViewLayoutRecord({
          children: [actionViewId]
        })
        : state;
    }
    case actionTypes.SET_LAYOUT_TWO_VIEW_STACK:
    case actionTypes.SET_LAYOUT_TWO_VIEW_STACK_THIRDS:
      return setViewsLayoutOfNumber(2, state, viewsCount, activeViewId, [action.viewId], ORIENTATION_ROW);
    case actionTypes.SET_LAYOUT_TWO_VIEW_SIDE:
      return setViewsLayoutOfNumber(2, state, viewsCount, activeViewId, [action.viewId], ORIENTATION_COLUMN);
    case actionTypes.REMOVE_OTHER_VIEWS: {
      newLayout = newSubLayout(ORIENTATION_ROW, [actionViewId]);
      consolidateRequired = true;
      break;
    }
    case actionTypes.LOCK_SCROLLING:
      scrollingLocked = true;
      consolidateRequired = true;
      break;
    case actionTypes.UNLOCK_SCROLLING:
      scrollingLocked = false;
      consolidateRequired = true;
      break;
    case actionTypes.SWITCH_VIEW_TYPE: {
      // Do not remove the last child
      consolidateRequired = true;
      break;
    }

    default:
      break;
  }

  if (consolidateRequired) {
    // Consolidate the views into the optimal possible layout...
    const viewTypesAfterAction = getViewTypesFollowingAction(prevGlobalState, action, actionViewId).toJS();
    newLayout = consolidateLayout(newLayout, 0, viewTypesAfterAction, scrollingLocked);
  }

  // No consolation required, so we can just return newLayout directly.
  return newLayout;
};

export default layout;
